import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  MenuItem,
} from '@mui/material';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { DocumentData } from 'firebase/firestore';

interface EditCourseDialogProps {
  open: boolean;
  onClose: () => void;
  course: DocumentData | null;
}

export function EditCourseDialog({ open, onClose, course }: EditCourseDialogProps) {
  const { currentTenantId } = useTenantStore();
  const { updateCourse, isUpdatingCourse } = useAcademic();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [credits, setCredits] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'archived'>('active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && course) {
      setName(course.name || '');
      setCode(course.code || '');
      setCredits(course.credits || '');
      setDescription(course.description || '');
      setStatus(course.status || 'active');
      setError(null);
    }
  }, [open, course]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !course) return;

    setError(null);
    try {
      await updateCourse({
        tenantId: currentTenantId,
        courseId: course.id,
        name,
        code,
        credits: Number(credits) || 0,
        description,
        status,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error updating course');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Editar Curso</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Nombre del Curso"
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <TextField
              label="Código"
              required
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            <TextField
              label="Créditos"
              type="number"
              required
              fullWidth
              value={credits}
              onChange={(e) => setCredits(e.target.value ? Number(e.target.value) : '')}
            />

            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              select
              label="Estado"
              required
              fullWidth
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              <MenuItem value="active">Activo</MenuItem>
              <MenuItem value="inactive">Inactivo</MenuItem>
              <MenuItem value="archived">Archivado</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={isUpdatingCourse}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUpdatingCourse}
            sx={{ minWidth: 120 }}
          >
            {isUpdatingCourse ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
